import fs from "node:fs";
import path from "node:path";

import {
  getBundledChromiumCandidates,
  getWindowsBrowserCandidates,
  type BrowserPathCandidate
} from "../constants/browserPaths";
import { PdfEngineError, type DetectedBrowser } from "../types/types";

export type BrowserDetectionOptions = {
  executablePath?: string;
  cwd?: string;
  env?: NodeJS.ProcessEnv;
  platform?: NodeJS.Platform;
  processExecPath?: string;
  fileExists?: (filePath: string) => boolean;
};

function defaultFileExists(filePath: string): boolean {
  try {
    return fs.statSync(filePath).isFile();
  } catch {
    return false;
  }
}

function assertSafeExecutablePath(executablePath: string, source: string): void {
  if (executablePath.includes("\0")) {
    throw new PdfEngineError(
      "VALIDATION_ERROR",
      `${source} browser executable path contains an invalid null byte.`
    );
  }
}

function toDetectedBrowser(candidate: BrowserPathCandidate): DetectedBrowser {
  return {
    name: candidate.name,
    source: candidate.source,
    executablePath: path.resolve(candidate.executablePath)
  };
}

export function detectBrowser(options: BrowserDetectionOptions = {}): DetectedBrowser {
  const env = options.env ?? process.env;
  const cwd = options.cwd ?? process.cwd();
  const platform = options.platform ?? process.platform;
  const processExecPath = options.processExecPath ?? process.execPath;
  const fileExists = options.fileExists ?? defaultFileExists;
  const triedPaths: string[] = [];

  if (options.executablePath) {
    assertSafeExecutablePath(options.executablePath, "Configured");

    if (!fileExists(options.executablePath)) {
      throw new PdfEngineError(
        "VALIDATION_ERROR",
        "Configured browser executablePath does not exist or is not a file.",
        {
          details: {
            executablePath: options.executablePath
          }
        }
      );
    }

    return {
      name: "Configured Chromium",
      source: "override",
      executablePath: path.resolve(options.executablePath)
    };
  }

  const installedCandidates =
    platform === "win32" ? getWindowsBrowserCandidates(env) : [];
  const bundledCandidates = getBundledChromiumCandidates(env, cwd, processExecPath);
  const candidates = [...installedCandidates, ...bundledCandidates];

  for (const candidate of candidates) {
    assertSafeExecutablePath(candidate.executablePath, candidate.name);
    triedPaths.push(candidate.executablePath);

    if (fileExists(candidate.executablePath)) {
      return toDetectedBrowser(candidate);
    }
  }

  throw new PdfEngineError(
    "BROWSER_NOT_FOUND",
    "No local Chrome, Edge, or bundled Chromium executable was found.",
    {
      details: {
        triedPaths
      }
    }
  );
}
